import { dayjs } from "@/core/utils/date";
import type { IActiveAiScriptRun } from "@/payroll/ai-script/types";
import { getAiScriptPayrollRunStatus } from "@/payroll/ai-script/utils";
import { PAYROLL_RUN_STATUS } from "@/payroll/constants";
import { LOCKED_RERUN_REASON } from "@/payroll/pay-periods/pay-period-lock";
import type {
  EnrichedPayPeriod,
  IPayrollRun,
  PayrollRunStatus,
} from "@/payroll/payroll.d";
import {
  canTriggerNewRun,
  isPayPeriodLocked,
} from "@/payroll/utils/period-helpers";
import type {
  AiScriptRerunRunOptions,
  AiScriptRerunSelection,
  AiScriptRerunState,
  AiScriptRerunVersionInfo,
  PayBreakdownCalculationState,
  PayBreakdownOutput,
  PayBreakdownPeriod,
  PayBreakdownRerunAction,
  PayBreakdownRunTarget,
  PayBreakdownVersionContext,
} from "./types";
import {
  AI_SCRIPT_RERUN_VERSION_CHOICE,
  PAY_BREAKDOWN_CALCULATION_STATE,
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
} from "./types";

export const getLatestCompletedLegacyRun = (
  runs: IPayrollRun[] | undefined,
): IPayrollRun | null => {
  if (!runs) return null;

  return (
    [...runs]
      .filter(
        (run) =>
          run.status === PAYROLL_RUN_STATUS.completed && !!run.result_file_url,
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
  );
};

export const applyPayBreakdownStatusToPeriods = (
  periods: PayBreakdownPeriod[],
  payPeriods: EnrichedPayPeriod[],
): PayBreakdownPeriod[] => {
  const payPeriodByStart = new Map(
    payPeriods.map((period) => [period.start_date, period]),
  );

  return periods.map((period) => {
    const payPeriod = payPeriodByStart.get(period.period_start);
    if (!payPeriod) return period;

    const aiStatus = getAiScriptPayrollRunStatus(
      payPeriod.latest_ai_script_run_status,
    );
    const status =
      aiStatus ??
      payPeriod.latest_icm_run_status ??
      payPeriod.latest_run_status;
    const hasPayrollRun =
      !!aiStatus ||
      !!payPeriod.latest_completed_ai_script_run_id ||
      payPeriod.icm_runs_count > 0 ||
      payPeriod.runs_count > 0;

    return {
      ...period,
      has_payroll_run: hasPayrollRun,
      latest_icm_run_status: status,
      is_locked: isPayPeriodLocked(payPeriod),
    };
  });
};

export const getDefaultPayBreakdownPeriod = (
  periods: PayBreakdownPeriod[],
  selectedPeriodStart?: string,
): PayBreakdownPeriod | undefined => {
  if (selectedPeriodStart) return undefined;

  return periods.find((period) => dayjs().isAfter(period.period_end, "day"));
};

export const getPayBreakdownOutput = ({
  hasGeneratedWorkbook,
  selectedAiRunId,
  selectedIcmRunId,
  latestLegacyRun,
}: {
  hasGeneratedWorkbook: boolean;
  selectedAiRunId: string | null;
  selectedIcmRunId: string | null;
  latestLegacyRun: IPayrollRun | null;
}): PayBreakdownOutput => {
  if (hasGeneratedWorkbook && selectedAiRunId) {
    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
      runId: selectedAiRunId,
    };
  }

  if (selectedIcmRunId) {
    return { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.icm, runId: selectedIcmRunId };
  }

  if (latestLegacyRun) {
    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript,
      run: latestLegacyRun,
    };
  }

  return { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.none };
};

export const getPayBreakdownLatestRunAt = (
  output: PayBreakdownOutput,
  selectedPayPeriod: EnrichedPayPeriod | undefined,
) => {
  switch (output.type) {
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript:
      return selectedPayPeriod?.latest_ai_script_run_at;
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.icm:
      return selectedPayPeriod?.latest_icm_run_at;
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript:
      return output.run.created_at;
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.none:
      return (
        selectedPayPeriod?.latest_ai_script_run_at ??
        selectedPayPeriod?.latest_icm_run_at ??
        selectedPayPeriod?.latest_run_at
      );
  }
};

export const hasActiveAiRunAffectingVisibleSheets = (
  activeRuns: IActiveAiScriptRun[] | undefined,
) =>
  // A missing flag means visible so an older API over-notifies instead of
  // hiding a run that changes this user's results.
  (activeRuns ?? []).some((run) => run.affects_visible_sheets !== false);

interface GetPayBreakdownCalculationStateParams {
  hasSelectedPeriod: boolean;
  hasActiveRun: boolean;
  hasOutput: boolean;
  activeRunAffectsVisibleOutput: boolean;
}

export const getPayBreakdownCalculationState = ({
  hasSelectedPeriod,
  hasActiveRun,
  hasOutput,
  activeRunAffectsVisibleOutput,
}: GetPayBreakdownCalculationStateParams): PayBreakdownCalculationState => {
  if (!hasSelectedPeriod || !hasActiveRun) {
    return PAY_BREAKDOWN_CALCULATION_STATE.idle;
  }

  if (!hasOutput) {
    return activeRunAffectsVisibleOutput
      ? PAY_BREAKDOWN_CALCULATION_STATE.initial
      : PAY_BREAKDOWN_CALCULATION_STATE.background;
  }

  return activeRunAffectsVisibleOutput
    ? PAY_BREAKDOWN_CALCULATION_STATE.rerun
    : PAY_BREAKDOWN_CALCULATION_STATE.background;
};

export const getLegacyRunFilename = (run: IPayrollRun) =>
  run.result_file_metadata?.filename ?? "payroll.xlsx";

export const getPayBreakdownVersionContext = (
  selectedPayPeriod: EnrichedPayPeriod | undefined,
  payPeriods: EnrichedPayPeriod[],
): PayBreakdownVersionContext => {
  const rosterVersionNumber = selectedPayPeriod?.roster_version_number ?? null;
  const currentRosterVersionNumber =
    selectedPayPeriod?.current_roster_version_number ??
    payPeriods.find((period) => period.current_roster_version_number != null)
      ?.current_roster_version_number ??
    null;
  const currentScriptVersionNumber =
    selectedPayPeriod?.current_ai_script_version_number ??
    payPeriods.find((period) => period.current_ai_script_version_number != null)
      ?.current_ai_script_version_number ??
    null;

  return {
    rosterVersionNumber,
    currentRosterVersionNumber,
    rerunVersionInfo: {
      runScriptVersion: selectedPayPeriod?.ai_script_version_number ?? null,
      runRosterVersion: rosterVersionNumber,
      currentScriptVersion: currentScriptVersionNumber,
      currentRosterVersion: currentRosterVersionNumber,
      runScriptVersionId:
        selectedPayPeriod?.latest_completed_ai_script_run_version_id ?? null,
      runRosterId:
        selectedPayPeriod?.latest_completed_ai_script_run_roster_id ?? null,
    },
  };
};

export const getAiScriptRerunState = (
  versionInfo: AiScriptRerunVersionInfo,
): AiScriptRerunState => {
  const scriptDrift =
    versionInfo.runScriptVersion != null &&
    versionInfo.currentScriptVersion != null &&
    versionInfo.runScriptVersion !== versionInfo.currentScriptVersion;
  const rosterDrift =
    versionInfo.runRosterVersion != null &&
    versionInfo.currentRosterVersion != null &&
    versionInfo.runRosterVersion !== versionInfo.currentRosterVersion;

  return {
    scriptDrift,
    rosterDrift,
    hasDrift: scriptDrift || rosterDrift,
    scriptVersion:
      versionInfo.runScriptVersion ?? versionInfo.currentScriptVersion,
    rosterVersion:
      versionInfo.runRosterVersion ?? versionInfo.currentRosterVersion,
  };
};

export const getMissingAiScriptRerunVersionReferences = ({
  versionInfo,
  scriptChoice,
  rosterChoice,
  isScopedRerun,
}: AiScriptRerunSelection) => {
  const { scriptDrift, rosterDrift } = getAiScriptRerunState(versionInfo);

  return [
    ...(scriptDrift &&
    (isScopedRerun || scriptChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep) &&
    versionInfo.runScriptVersionId === null
      ? ["script"]
      : []),
    ...(rosterDrift &&
    rosterChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep &&
    versionInfo.runRosterId === null
      ? ["roster"]
      : []),
  ];
};

export const buildAiScriptRerunOptions = (
  selection: AiScriptRerunSelection,
): AiScriptRerunRunOptions | undefined => {
  const { versionInfo, scriptChoice, rosterChoice, isScopedRerun } = selection;
  const { scriptDrift, rosterDrift, hasDrift } =
    getAiScriptRerunState(versionInfo);
  if (!hasDrift) return undefined;

  const missingVersionReferences =
    getMissingAiScriptRerunVersionReferences(selection);
  if (missingVersionReferences.length > 0) {
    throw new Error(
      `Cannot rerun payroll: missing historical ${missingVersionReferences.join(
        " and ",
      )} version reference`,
    );
  }

  return {
    ...(scriptDrift &&
    (isScopedRerun || scriptChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep)
      ? { aiScriptVersionId: versionInfo.runScriptVersionId! }
      : {}),
    ...(rosterDrift && rosterChoice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep
      ? { payrollRosterId: versionInfo.runRosterId! }
      : {}),
  };
};

export interface GetPayBreakdownRunTargetParams {
  selectedPeriod: PayBreakdownPeriod | undefined;
  selectedPeriodForCard: EnrichedPayPeriod | undefined;
  latestScriptRunStatus: PayrollRunStatus | null;
  latestIcmRunStatus: PayrollRunStatus | null;
  hasActiveRun: boolean;
  isLocked: boolean;
  hasAiScript: boolean;
  hasIcmAccess: boolean;
  hasPayrollScript: boolean;
  icmRunDisabledReason: string | undefined;
  /**
   * Whether the user may rerun the WHOLE payroll. A department lead assigned one
   * sheet may not; they rerun their own groups instead.
   */
  canRerunWholePayroll: boolean;
  /** Whether the user may start the first AI run for this pay period. */
  canStartInitialPayroll?: boolean;
  /**
   * Whether the user has at least one group they may rerun on their own. This is
   * what keeps the single rerun button reachable for a department lead, who has
   * no whole-payroll authority but plenty to rerun.
   */
  hasRerunUnits?: boolean;
}

export const getPayBreakdownRunTarget = ({
  selectedPeriod,
  selectedPeriodForCard,
  latestScriptRunStatus,
  latestIcmRunStatus,
  hasActiveRun,
  isLocked,
  hasAiScript,
  hasIcmAccess,
  hasPayrollScript,
  icmRunDisabledReason,
  canRerunWholePayroll,
  canStartInitialPayroll = false,
  hasRerunUnits = false,
}: GetPayBreakdownRunTargetParams): PayBreakdownRunTarget => {
  const activeRunDisabledReason = hasActiveRun
    ? "Payroll is already running for this period"
    : undefined;
  const disabledReason = isLocked
    ? LOCKED_RERUN_REASON
    : activeRunDisabledReason;

  if (hasAiScript) {
    const canAccessRerun =
      !!selectedPeriod &&
      (canRerunWholePayroll || hasRerunUnits || canStartInitialPayroll);

    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
      canAccessRerun,
      canRunWholePayroll: canRerunWholePayroll,
      // A department lead cannot run the WHOLE payroll but can rerun their own
      // groups, and the one rerun button is now their only route to it — gating
      // the button on whole-payroll authority left them unable to rerun at all.
      // The dialog offers them their units and nothing else, and the API
      // re-checks every tab in the scope on both create and dequeue.
      canRunPeriod: canAccessRerun && !isLocked && !hasActiveRun,
      disabledReason,
    };
  }

  if (hasIcmAccess) {
    const canRunIcmPeriod =
      !!selectedPeriodForCard &&
      !icmRunDisabledReason &&
      (!selectedPeriodForCard.existsInDb ||
        canTriggerNewRun(selectedPeriodForCard, latestIcmRunStatus));

    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.icm,
      canRunPeriod: !isLocked && !hasActiveRun && canRunIcmPeriod,
      disabledReason: disabledReason ?? icmRunDisabledReason,
    };
  }

  if (hasPayrollScript) {
    const canRunLegacyPeriod =
      !!selectedPeriodForCard &&
      (!selectedPeriodForCard.existsInDb ||
        canTriggerNewRun(selectedPeriodForCard, latestScriptRunStatus));

    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript,
      canRunPeriod: !isLocked && !hasActiveRun && canRunLegacyPeriod,
      disabledReason,
    };
  }

  return { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.none, canRunPeriod: false };
};

interface GetPayBreakdownRerunActionParams {
  runTarget: PayBreakdownRunTarget;
  hasActiveRun: boolean;
  showIcmRerunControls: boolean;
  isCreatingAiRun: boolean;
  isCreatingPeriod: boolean;
  isTriggeringRun: boolean;
  isIcmRunActionRunning: boolean;
  onRunPreferredPayroll: () => void;
}

export const getPayBreakdownRerunAction = ({
  runTarget,
  hasActiveRun,
  showIcmRerunControls,
  isCreatingAiRun,
  isCreatingPeriod,
  isTriggeringRun,
  isIcmRunActionRunning,
  onRunPreferredPayroll,
}: GetPayBreakdownRerunActionParams): PayBreakdownRerunAction => {
  switch (runTarget.type) {
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript:
      return {
        // Shown to anyone with something to rerun. A department lead cannot run
        // the whole payroll, but the dialog behind this button offers them their
        // own groups — so hiding it on whole-payroll authority alone would leave
        // them with no route to a rerun at all.
        isVisible: runTarget.canAccessRerun,
        onRun: onRunPreferredPayroll,
        canRunPeriod: runTarget.canRunPeriod,
        isRunning: isCreatingAiRun || hasActiveRun,
        disabledReason: runTarget.disabledReason,
      };
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript:
      return {
        isVisible: true,
        onRun: onRunPreferredPayroll,
        canRunPeriod: runTarget.canRunPeriod,
        isRunning: isCreatingPeriod || isTriggeringRun || hasActiveRun,
        disabledReason: runTarget.disabledReason,
      };
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.icm:
      return {
        isVisible: showIcmRerunControls,
        onRun: onRunPreferredPayroll,
        canRunPeriod: runTarget.canRunPeriod,
        isRunning: isIcmRunActionRunning,
        disabledReason: runTarget.disabledReason,
      };
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.none:
      return {
        isVisible: false,
        onRun: onRunPreferredPayroll,
        canRunPeriod: false,
        isRunning: false,
        disabledReason: runTarget.disabledReason,
      };
  }
};

/**
 * Whether a run rewrites the WHOLE workbook.
 *
 * Three states, not two. `null`/absent means a full-workbook run. An EMPTY array
 * means a scoped run whose tabs are all hidden from this user — the API redacts the
 * names rather than confirm which departments exist, and deliberately keeps the run
 * in the payload so the UI knows the period is busy. Reading `[]` as "full" would
 * let another department's scoped rerun disable this user's only rerun control, when
 * scoped runs are meant to queue behind each other.
 */
export const isFullWorkbookRun = (run: IActiveAiScriptRun): boolean =>
  run.sheet_scope == null;

/** "A", "A and B", "A, B and C" */
export const formatList = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];

  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};
